import { NavLink, matchPath, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { NavLeafNode, NavCtx } from '../nav-config';
import { resolveTo } from '../nav-config';
import { MockPill } from '@components/ui/MockPill/MockPill';
import styles from './styles.module.css';

type INavLeafProps = { leaf: NavLeafNode; ctx: NavCtx };

export function NavLeaf({ leaf, ctx }: INavLeafProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const to = resolveTo(leaf.to, ctx);
  const title = t(leaf.labelKey);

  // Some pages redirect to a URL deeper than `to`.
  // `matchTo` keeps the leaf highlighted on those routes.
  const matchesExtra = (leaf.matchTo ?? []).some((pattern) =>
    matchPath({ path: resolveTo(pattern, ctx), end: true }, pathname),
  );

  const content = (
    <>
      <span className={styles.icon}>{leaf.icon}</span>
      <span className={styles.text}>
        <span className={styles.titleRow}>
          <span className={styles.title}>{title}</span>
          {leaf.mock ? <MockPill kind="badge" /> : null}
        </span>
      </span>
    </>
  );

  if (leaf.disabled) {
    return (
      <span className={`${styles.leaf} ${styles.leafDisabled}`} aria-disabled="true" title={title}>
        {content}
      </span>
    );
  }

  return (
    <NavLink
      to={to}
      end
      title={title}
      className={({ isActive }) =>
        `${styles.leaf} ${isActive || matchesExtra ? styles.leafActive : ''}`
      }
    >
      {content}
    </NavLink>
  );
}
